import { Link } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Compass, Home, Search } from 'lucide-react'
import { Section } from '@/shared/ui/section'
import { Button } from '@/shared/ui/button'
import { Seo } from '@/shared/seo/seo'
import { defineT, useLocale, useTranslation } from '@/shared/i18n/use-translation'
import { asset } from '@/shared/lib/asset'

const T = defineT({
  pageTitle: { ar: 'الصفحة غير موجودة', en: 'Page not found' },
  eyebrow: { ar: '404', en: '404' },
  title: { ar: 'الصفحة دي ضاعت في الفضا', en: 'This page wandered off' },
  body: {
    ar: 'يمكن نقلتها، يمكن غيّرنا اسمها، أو يمكن الرابط فيه حرف زيادة. ماهيش نهاية الدنيا — جرّب من هنا.',
    en: 'It might have moved, been renamed, or the URL has a typo. Either way — try one of these.',
  },
  home: { ar: 'العودة للرئيسية', en: 'Back home' },
  browse: { ar: 'تصفّح المحتوى', en: 'Browse content' },
  // Suggestion cards
  s1Title: { ar: 'الدورات', en: 'Courses' },
  s1Desc: { ar: 'كل الدورات المتاحة', en: 'All available courses' },
  s2Title: { ar: 'البرومبتات', en: 'Prompts' },
  s2Desc: { ar: 'مكتبة برومبتات جاهزة', en: 'Curated prompt library' },
  s3Title: { ar: 'المقالات', en: 'Blog' },
  s3Desc: { ar: 'كتابات في الذكاء الاصطناعي', en: 'AI essays and tutorials' },
})

export default function NotFoundPage() {
  const { t } = useTranslation(T)
  const { isRtl } = useLocale()
  const Arrow = isRtl ? ArrowLeft : ArrowRight

  const suggestions = [
    { to: '/courses', titleKey: 's1Title' as const, descKey: 's1Desc' as const },
    { to: '/prompts', titleKey: 's2Title' as const, descKey: 's2Desc' as const },
    { to: '/blog', titleKey: 's3Title' as const, descKey: 's3Desc' as const },
  ]

  return (
    <>
      <Seo title={t('pageTitle')} path="/404" noIndex />
      <Section pad="lg" className="relative isolate overflow-hidden">
        {/* Decorative aurora */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10"
        >
          <div className="absolute left-1/2 top-1/4 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-gradient-to-br from-primary/30 via-primary/10 to-transparent opacity-40 blur-3xl" />
        </div>

        <div className="mx-auto max-w-2xl text-center">
          {/* Circular illustration with orbiting glow rings */}
          <div className="relative mx-auto mb-8 h-40 w-40 sm:h-48 sm:w-48">
            {/* Outer pulse ring */}
            <span
              aria-hidden
              className="absolute inset-0 animate-pulse-glow rounded-full bg-primary/20 blur-2xl"
            />
            {/* Static decorative rings */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-full border border-primary/20"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-3 rounded-full border border-primary/10"
            />
            {/* Image core — circular crop */}
            <div className="absolute inset-6 overflow-hidden rounded-full border-2 border-primary/30 shadow-[0_0_40px_-10px_hsl(var(--primary)/0.5)]">
              <img
                src={asset('/not-found-illustration.webp')}
                alt=""
                aria-hidden
                loading="eager"
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            </div>
            {/* Glow accent */}
            <span
              aria-hidden
              className="pointer-events-none absolute -bottom-2 left-1/2 h-1 w-24 -translate-x-1/2 rounded-full bg-primary/50 blur-sm"
            />
          </div>

          {/* Big 404 — kept smaller now since the image carries the impact */}
          <p className="font-headline text-7xl leading-none text-gradient sm:text-8xl">
            404
          </p>

          <span className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 font-headline text-[10px] uppercase tracking-[0.18em] text-primary">
            <Compass className="h-3 w-3" aria-hidden />
            {t('eyebrow')}
          </span>

          <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">
            {t('title')}
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
            {t('body')}
          </p>

          <div className="mt-7 flex flex-col items-center justify-center gap-2 sm:flex-row">
            <Link to="/">
              <Button size="lg">
                <Home className="h-4 w-4" aria-hidden />
                {t('home')}
                <Arrow className="h-4 w-4" aria-hidden />
              </Button>
            </Link>
            <Link to="/courses">
              <Button size="lg" variant="outline">
                <Search className="h-4 w-4" aria-hidden />
                {t('browse')}
              </Button>
            </Link>
          </div>

          {/* Suggestion grid */}
          <div className="mt-10 grid gap-3 sm:grid-cols-3">
            {suggestions.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="group/sug rounded-2xl border border-border bg-card/60 p-4 text-start transition-colors hover:border-primary/40 hover:bg-primary/5"
              >
                <p className="text-sm font-semibold text-foreground">
                  {t(s.titleKey)}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {t(s.descKey)}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </Section>
    </>
  )
}
